import {Injectable, RendererFactory2} from '@angular/core';
import {NavigationEnd, Router, Event} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {filter} from "rxjs/operators";

declare let advisorTrackPageview: any;

/**
 * Service with helper functions for sending tracking information to external tracker like matomo or google analytics.
 */
@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  /**
   * Some constants for event tracking
   */
  static readonly EVENT_CATEGORY_OVERLAY = 'overlay';
  static readonly EVENT_ACTION_CLOSE = 'close';

  /**
   * List of elements that are used to count an interaction (if they are focused)
   */
  static readonly VISIT_ELEMENTS = ['button', 'a', 'input', 'label', 'select'];

  /**
   * the currently active advisor.
   */
  advisor: string;

  /**
   * Handler for tracking the visit, but only one time.
   */
  visitHandler: any;

  /**
   * constructor
   * @param router
   * @param rendererFactory2
   * @param http
   */
  constructor(private router: Router, private rendererFactory2: RendererFactory2, private http: HttpClient) {}

  /**
   * Initialize the service
   */
  init(): void {
    // on routing events track the pageviews
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageview(event.url);

      if(!this.advisor) {
        // try to get the advisors name from the URL (second part, without parameter)
        const urlParts = event.url.split("/");
        if(urlParts?.length > 2) {
          this.advisor = urlParts[2];
          if(this.advisor.indexOf("?") > -1) {
            this.advisor = this.advisor.substring(0, this.advisor.indexOf("?"));
          }
        }
      }
    });

    // track visits by listening for the first interaction wth the tool.
    const renderer = this.rendererFactory2.createRenderer(null, null);
    this.visitHandler = renderer.listen('document', 'click', (event) => {
      if(TrackingService.VISIT_ELEMENTS.indexOf(event.target.nodeName.toLowerCase()) !== -1 && this.advisor) {
        this.trackVisit();

        // only track the visit once, therefore stop listening now.
        this.visitHandler();
      }
    });
  }

  /**
   * returns a date string of the current date
   * @private
   */
  private getTodayString(): string {
    const now = new Date();
    return now.getFullYear() + '-' + now.getUTCMonth() + '-' + now.getUTCDay();
  }

  /**
   * tracks a specific url as pageview.
   * @param url
   */
  trackPageview(url: string): void {
    if(typeof advisorTrackPageview !== "undefined") {
      advisorTrackPageview();
    }
  }

  /**
   * tracks a visit (first interaction with the tool to the backend)
   */
  trackVisit(): void {
    // check if we did this already today
    try {
      if (typeof localStorage === "object") {
        const lastVisit = localStorage.getItem('advisor-visit-' + this.advisor);
        if (lastVisit === this.getTodayString()) {
          // do not track again
          return;
        }
      }
    } catch (e) {
      // do not count, no local storage available
      return;
    }

    // track the visit
    this.http.post('/visit/' + this.advisor, []).subscribe(result => {
      if(typeof localStorage === "object") {
        localStorage.setItem('advisor-visit-' + this.advisor, this.getTodayString());
      }
    });
  }

}
